import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { useMutation, useQuery } from 'react-apollo';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Button from '../Button';
import DismissButton from '../DismissButton';
import Select from '../Select';
import FormGroup from '../FormGroup';
import PhoneInput from '../PhoneInput';
import Alert from '../Alert';
import { FeedbackCreate as FeedbackCreateMutation } from '../../product/SupportModal/graphql/mutations';
import { getAsset } from '../../../util/assets';
import useOpenAlert from '../../../hooks/useOpenAlert';
import { getErrorString } from '../../../util/normalize-errors';
import { MeQuery } from '../../../graphql/queries/account';
import { isValidEmail } from '../../../util/email';
import { isValidPhone } from '../../../util/phone';
import ConsumerRootContentSearch from './components/ConsumerRootContentSearch';
import {
  ISSUE_CATEGORIES,
  ISSUE_CATEGORY_PLACEHOLDER,
  ISSUE_DETAIL_PLACEHOLDER,
  getIssueCategoryLabel,
  getIssueDetailLabel,
  getIssueDetails,
} from './constants';

const propTypes = {
  onClose: PropTypes.func,
  onRequestClose: PropTypes.func,
  dismissible: PropTypes.bool,
};

const ConsumerFeedback = ({ onClose, onRequestClose, dismissible = true }) => {
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const location = useLocation();
  const openAlert = useOpenAlert();
  const authenticated = useSelector(({ auth }) => auth.authenticated);
  const { data: { me: user } = {} } = useQuery(MeQuery, {
    skip: !authenticated,
  });
  const [onFeedbackCreate] = useMutation(FeedbackCreateMutation);

  const onSubmit = async values => {
    if (submitting) {
      return;
    }

    const issueCategoryLabel = getIssueCategoryLabel(values.issue_category);
    const issueDetailLabel = getIssueDetailLabel(
      values.issue_category,
      values.issue_detail,
    );
    const message = [
      `title: ${issueCategoryLabel ?? 'N/A'}`,
      `issue subcategory: ${issueDetailLabel ?? 'N/A'}`,
      `mobile number: ${values.phone_number ?? 'N/A'}`,
      `description: ${values.description ?? 'N/A'}`,
      `hierarchy_item_id: ${values.hierarchy_item_id ?? 'N/A'}`,
    ].join('\n');

    setSubmitting(true);
    try {
      await onFeedbackCreate({
        variables: {
          input: {
            platform: 'WEB',
            url: location.pathname,
            email_address: values.email_address,
            message,
          },
        },
      });

      setSuccess(true);
    } catch (e) {
      openAlert({
        type: 'error',
        message: getErrorString(e),
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <Fragment>
        {dismissible && (
          <div className="text-right">
            <DismissButton onClick={onClose ?? onRequestClose} />
          </div>
        )}
        <div className="text-center mb-2">
          <img
            src={getAsset('/waldo-excited.png')}
            width="40px"
            alt="Waldo Exited"
          />
        </div>
        <p className="text-center h3">Thank you for your feedback!</p>
      </Fragment>
    );
  }

  return (
    <Fragment>
      {dismissible && (
        <div className="text-right">
          <DismissButton onClick={onClose ?? onRequestClose} />
        </div>
      )}
      <Formik
        onSubmit={onSubmit}
        enableReinitialize
        validate={values => {
          const errors = {};

          if (!values.issue_category) {
            errors.issue_category = 'Please select an issue category';
          }

          if (values.issue_category && !values.issue_detail) {
            errors.issue_detail = 'Please select a more specific issue';
          }

          if (!values.phone_number || !isValidPhone(values.phone_number)) {
            errors.phone_number = 'Please enter a valid mobile number';
          }

          if (!values.email_address || !isValidEmail(values.email_address)) {
            errors.email_address = 'Please enter a valid email address';
          }

          return errors;
        }}
        initialValues={{
          issue_category: '',
          issue_detail: '',
          phone_number: user?.phone_number ?? '+1',
          description: '',
          email_address: user?.email_address ?? '',
          hierarchy_item_id: '',
        }}
        render={({
          values,
          errors,
          handleChange,
          setFieldValue,
          handleSubmit,
          submitCount,
        }) => {
          const errorList = Object.values(errors);
          const issueDetails = getIssueDetails(values.issue_category);

          return (
            <Fragment>
              <h4 className="text-center mb-3">Give us some feedback</h4>

              <form onSubmit={handleSubmit}>
                <ConsumerRootContentSearch
                  values={values}
                  handleChange={handleChange}
                  prefillMostRecent
                />

                <Select
                  id="issue_category"
                  name="issue_category"
                  label="What issue are you facing?"
                  value={values.issue_category}
                  onChange={({ target }) => {
                    setFieldValue('issue_category', target.value);
                    setFieldValue('issue_detail', '');
                  }}
                  className="mb-2"
                >
                  <option value="">{ISSUE_CATEGORY_PLACEHOLDER}</option>
                  {ISSUE_CATEGORIES.map(category => (
                    <option key={category.key} value={category.key}>
                      {category.label}
                    </option>
                  ))}
                </Select>

                {values.issue_category && (
                  <Select
                    id="issue_detail"
                    name="issue_detail"
                    label="What best describes your issue?"
                    value={values.issue_detail}
                    onChange={handleChange}
                    className="mb-2"
                  >
                    <option value="">{ISSUE_DETAIL_PLACEHOLDER}</option>
                    {issueDetails.map(detail => (
                      <option key={detail.key} value={detail.key}>
                        {detail.label}
                      </option>
                    ))}
                  </Select>
                )}

                <PhoneInput
                  id="phone_number"
                  label="Mobile Number"
                  value={values.phone_number}
                  onChange={phone => {
                    setFieldValue('phone_number', phone);
                  }}
                  className="mb-2"
                />

                <FormGroup
                  id="email_address"
                  label="Email Address"
                  placeholder="Insert your email"
                  value={values.email_address}
                  onChange={handleChange}
                  className="mb-2"
                />

                <FormGroup
                  id="description"
                  label={
                    <p className="text-light text-xs text-bold text-uppercase mb-0">
                      Description{' '}
                      <span className="text-primary">(Optional)</span>
                    </p>
                  }
                  type="textarea"
                  placeholder="Describe your issue here"
                  value={values.description}
                  onChange={handleChange}
                  className="mb-3"
                />

                {submitCount > 0 && errorList.length > 0 && (
                  <Alert type="danger" className="mb-3">
                    <ul className="mb-0">
                      {errorList.map(error => (
                        <li key={error}>{error}</li>
                      ))}
                    </ul>
                  </Alert>
                )}

                <Button
                  type="submit"
                  btnBlock
                  disabled={
                    submitting || !values.issue_category || !values.issue_detail
                  }
                >
                  {submitting ? 'Submitting...' : 'Submit'}
                </Button>
              </form>
            </Fragment>
          );
        }}
      />
    </Fragment>
  );
};

ConsumerFeedback.propTypes = propTypes;

export default ConsumerFeedback;
